
import { strFromU8, strToU8, unzip, zip as zipSync } from "fflate";
import type { ApplyBrandOptions, ApplyBrandResult, BrandSpec, ShapeClassification, TableRowFormat } from "../../../types";
import {
  normalizeHex,
  luminance,
  isNearWhite,
  isNearBlack,
  isNeutralHex,
  blendWithWhite,
  isTextUnreadable,
  isDefaultFontColor,
  findNearestBrandColor,
  APPLY_BRAND_NEAR_WHITE_THRESHOLD,
  MID_LUMINANCE,
} from "../../../utils/colourUtils";

// Original font colour already this light/near-white — treated as "was
// never meaningfully dark to begin with" so a dark fill can still force a
// light replacement font colour below, rather than keeping a near-white
// original that would vanish against it.
const ORIGINAL_FONT_NEAR_WHITE_LUMINANCE = 220;
import { logError, logEvent, logOfficeError, scrubMessage } from "../../../../../telemetry";

// ─── PPTX zip (flat path → bytes map, decompressed via fflate) ───────────────

export type PptxZip = Record<string, Uint8Array>;


export function base64ToU8(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  // NOSONAR: atob() returns a binary string (0-255), so charCodeAt() is appropriate.
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.codePointAt(i) ?? 0;
  }

  return bytes;
}

export function u8ToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCodePoint(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

export function unzipPptxBase64(base64: string): Promise<PptxZip> {
  return new Promise((resolve, reject) => {
    unzip(base64ToU8(base64), (err, files) => (err ? reject(err) : resolve(files)));
  });
}

export function zipPptxToBase64(files: PptxZip): Promise<string> {
  return new Promise((resolve, reject) => {
    zipSync(files, { level: 6 }, (err, data) => (err ? reject(err) : resolve(u8ToBase64(data))));
  });
}

/**
 * Replace a slide in-place: insertSlidesFromBase64 inserts the modified copy
 * AFTER targetSlideId, so the ORIGINAL keeps its index — delete the original
 * by id afterward (deleting by index+1 would delete the freshly inserted
 * copy instead). Shared by brandFixer's OOXML auto-fixes and speaker notes'
 * export/rewrite/reinsert round-trip.
 */
export async function replaceSlideWithRewrittenCopy(
  context: PowerPoint.RequestContext,
  files: PptxZip,
  slideId: string,
  formatting: PowerPoint.InsertSlideFormatting,
): Promise<void> {
  const newBase64 = await zipPptxToBase64(files);
  context.presentation.insertSlidesFromBase64(newBase64, {
    targetSlideId: slideId,
    formatting,
  });
  await context.sync();

  const original = context.presentation.slides.getItemOrNullObject(slideId);
  original.load("id");
  await context.sync();
  if (!original.isNullObject) {
    original.delete();
    await context.sync();
  }
}

// ─── Constants ────────────────────────────────────────────────────────────────

// PowerPoint's standard slide HEIGHT in points is 540pt for BOTH 4:3
// (10in x 7.5in) and 16:9 widescreen (13.333in x 7.5in) layouts — only the
// width differs. Used as the geometry-classification fallback when a shape's
// actual slide height isn't threaded through (see classifyByGeometry,
// findTitleShape); the real height is read from the deck via
// brandExtractor's getSlideDimensions for anything extraction-side.
const SLIDE_HEIGHT_PTS = 540;

// Empirical threshold (85% of the target shape's own area) for treating a
// lower z-order shape as "covering" a text box above it (see
// findUnderlyingFill) — high enough to avoid false positives from shapes that
// merely intersect at an edge, low enough to tolerate minor edge padding.
const OVERLAP_COVERAGE_THRESHOLD = 0.85;

 // Blacklist: shape types that NEVER have a textFrame.
// Switched from whitelist to blacklist so ANY shape not explicitly excluded gets
// textFrame loading attempted — the old whitelist silently skipped many valid
// shapes (custom geometry, unsupported types that still render text, etc.).
const NO_TEXTFRAME_TYPES = new Set([
  "image", "Image", "picture", "Picture", "linkedImage", "LinkedImage",
  "line", "Line", "connector", "Connector",
  "contentApp", "ContentApp",
  "media", "Media", "model3D", "Model3D", "ole", "Ole", "diagram", "Diagram",
  "smartArt", "SmartArt", "graphic", "Graphic", "ink", "Ink",
  "unsupported", "Unsupported",
]);

// Groups have children shapes but no direct textFrame — processed via recursion.
const GROUP_TYPES = new Set(["group", "Group"]);

// These shape types have text but NOT via textFrame.
const SPECIAL_TEXT_TYPES = new Set([
  "Table", "table",
  "Chart", "chart",
]);

// Shapes with neither textFrame nor meaningful fill API.
// Tables and charts are included: their shape-level fill throws on load —
// their fills/fonts are handled by the OOXML post-processing pass instead.
const NO_FILL_TYPES = new Set([
  "line", "Line",
  "connector", "Connector",
  "contentApp", "ContentApp",
  "media", "Media", "model3D", "Model3D", "ole", "Ole", "diagram", "Diagram",
  "smartArt", "SmartArt", "graphic", "Graphic", "ink", "Ink",
  "table", "Table", "chart", "Chart",
  "unsupported", "Unsupported",
]);

// Shapes with no usable lineFormat API. Lines/connectors/images are NOT here —
// a connector's "fill" IS its line, and pictures can carry a brand border.
const NO_LINE_TYPES = new Set([
  "contentApp", "ContentApp",
  "media", "Media", "model3D", "Model3D", "ole", "Ole", "diagram", "Diagram",
  "smartArt", "SmartArt", "graphic", "Graphic", "ink", "Ink",
  "table", "Table", "chart", "Chart",
  "unsupported", "Unsupported",
]);

// ─── Office.js error-isolation helpers ───────────────────────────────────────
// A single invalid queued load rejects the ENTIRE batch sync, so proxy access
// is routinely wrapped: these helpers keep that swallowing in one place.

function errorMessage(e: unknown, fallback?: string): string {
  if (e instanceof Error) return e.message;
  return fallback ?? String(e);
}

/** connectorSystemName tag for all Deckorate apply-brand telemetry. */
const APPLY_BRAND_CONNECTOR = "deckorate";

/**
 * Report a whole-slide/whole-deck apply-brand failure through the shared
 * telemetry pipeline (PII-scrubbed) — mirrors the MapPlot feature's
 * logMapError convention (see MapPlot.tsx).
 */
function reportBrandError(reason: string, type: string, message: string): void {
  logError(
    "error.connector.failure",
    { type, scrubbedMessage: scrubMessage(message) },
    { connectorSystemName: APPLY_BRAND_CONNECTOR, reason },
  );
}

/**
 * Queue a proxy load/write; returns false if the proxy call itself throws.
 * Expected to fail routinely (e.g. loading a colour on a shape whose fill
 * type doesn't actually support it) — logged at debug level only so the
 * common case doesn't spam the console, while still being traceable.
 */
function tryQueue(queueLoad: () => void): boolean {
  try {
    queueLoad();
    return true;
  } catch {
    // Expected to fail routinely per-shape (see doc comment above) — the
    // `false` return is the handling; not telemetred to avoid flooding the
    // pipeline with a per-shape/per-property event.
    return false;
  }
}

/** Read a proxy property, returning null when the read throws (see {@link tryQueue}). */
function tryGet<T>(read: () => T): T | null {
  try {
    return read();
  } catch {
    return null;
  }
}

/** Sync the batch, swallowing failures (bad loads only disable themselves). */
async function syncQuiet(context: PowerPoint.RequestContext): Promise<void> {
  try {
    await context.sync();
  } catch {
    // Isolation is the handling — callers re-sync per shape afterwards.
  }
}

/** Queue a load and sync it in isolation; returns whether it succeeded. */
async function trySyncLoad(context: PowerPoint.RequestContext, queueLoad: () => void): Promise<boolean> {
  try {
    queueLoad();
    await context.sync();
    return true;
  } catch {
    return false;
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Apply brand formatting to the currently selected slide.
 */
export async function applyBrandToSlide(
  brand: BrandSpec,
  options: Partial<ApplyBrandOptions> = {},
): Promise<ApplyBrandResult> {
  const result: ApplyBrandResult = { slidesProcessed: 0, shapesUpdated: 0, actions: [], errors: [] };
  const onProgress = options.onProgress ?? (() => undefined);

  try {
    onProgress("Applying brand to current slide…", 0, 1);
    const stats = await applyToCurrentSlide(brand, options);
    result.slidesProcessed = 1;
    result.shapesUpdated = stats.shapesUpdated;
    result.changedSlideIndex = stats.slideIndex + 1;
    result.actions.push(`Applied brand fonts and colours to ${stats.shapesUpdated} shape(s)`);
    if (stats.aligned > 0) result.actions.push(`Fixed alignment on ${stats.aligned} shape(s)`);
    if (stats.autofit > 0) result.actions.push(`Enabled autofit on ${stats.autofit} shape(s)`);

    onProgress("Applying table and bullet formatting…", 0, 1);
    const ooxmlStats = await applyOoxmlViaExport(brand, "slide", options);
    if (ooxmlStats.tables > 0) result.actions.push(`Styled ${ooxmlStats.tables} table(s)`);
    if (ooxmlStats.bullets > 0) result.actions.push(`Updated ${ooxmlStats.bullets} bullet list(s)`);
    if (ooxmlStats.shadows > 0) result.actions.push(`Removed ${ooxmlStats.shadows} shadow(s)`);
    if (ooxmlStats.gradients > 0) result.actions.push(`Flattened ${ooxmlStats.gradients} gradient(s)`);
    if (ooxmlStats.animations > 0) result.actions.push(`Removed ${ooxmlStats.animations} animation(s)`);

    onProgress("Done", 1, 1);
  } catch (e) {
    const msg = e instanceof Error ? e.message : "Apply brand failed";
    reportBrandError("brand-apply-slide", "ApplyBrandError", msg);
    result.errors.push(msg);
  }

  return result;
}

/**
 * Apply brand formatting to all slides in the deck.
 */
export async function applyBrandToDeck(
  brand: BrandSpec,
  options: Partial<ApplyBrandOptions> = {},
): Promise<ApplyBrandResult> {
  const result: ApplyBrandResult = { slidesProcessed: 0, shapesUpdated: 0, actions: [], errors: [] };
  const onProgress = options.onProgress ?? (() => undefined);

  let slideCount = 0;
  let allShapes = 0;
  let allAligned = 0;
  let allAutofit = 0;

  try {
    await PowerPoint.run(async (context) => {
      const slides = context.presentation.slides;
      slides.load("items");
      await context.sync();
      slideCount = slides.items.length;

      const pass: DeckOoxmlPassContext = {
        brand, options, result, onProgress, slideCount,
        runState: { titleOnlySwapDone: false },
      };

      // Each slide's progress notification, Office.js formatting pass, and
      // OOXML post-processing are wrapped in a single try/catch so a failure
      // anywhere in that sequence (including an onProgress callback throwing)
      // only drops the current slide instead of aborting the whole deck.
      for (let i = 0; i < slides.items.length; i++) {
        const slide = slides.items[i]!;
        try {
          onProgress(`Applying brand to slide ${i + 1} of ${slideCount}…`, i, slideCount);
          const stats = await processSlide(context, slide, brand, null, options);
          allShapes += stats.shapesUpdated;
          allAligned += stats.aligned;
          allAutofit += stats.autofit;
          result.slidesProcessed++;
          await runOoxmlSlidePass(context, slide, i + 1, pass);
        } catch (e) {
          const msg = errorMessage(e, `Slide ${i + 1} failed`);
          reportBrandError("brand-apply-deck-slide", "ApplyBrandSlideError", msg);
          result.errors.push(msg);
        }
      }
    });

    result.shapesUpdated = allShapes;
    result.actions.unshift(`Applied brand to ${result.slidesProcessed} of ${slideCount} slide(s)`);
    if (allAligned > 0) result.actions.push(`Fixed alignment on ${allAligned} shape(s) across the deck`);
    if (allAutofit > 0) result.actions.push(`Enabled autofit on ${allAutofit} shape(s) across the deck`);
    onProgress("Done", slideCount, slideCount);
  } catch (e) {
    const msg = errorMessage(e, "Apply brand (deck) failed");
    reportBrandError("brand-apply-deck", "ApplyBrandError", msg);
    result.errors.push(msg);
  }

  return result;
}

/** Shared inputs for the per-slide OOXML pass in deck mode. */
interface DeckOoxmlPassContext {
  brand: BrandSpec;
  options: Partial<ApplyBrandOptions>;
  runState: { titleOnlySwapDone: boolean };
  result: ApplyBrandResult;
  onProgress: (message: string, current: number, total: number) => void;
  slideCount: number;
}

/** Per-slide OOXML post-processing for deck mode, recording actions on success. */
async function runOoxmlSlidePass(
  context: PowerPoint.RequestContext,
  slide: PowerPoint.Slide,
  slideNum: number,
  pass: DeckOoxmlPassContext,
): Promise<void> {
  try {
    pass.onProgress(`Formatting tables & bullets on slide ${slideNum}…`, slideNum - 1, pass.slideCount);
    const stats = await applyOoxmlToSlide(context, slide, pass.brand, pass.options, pass.runState);
    recordOoxmlSlideActions(pass.result, slideNum, stats);
  } catch {
    // OOXML post-processing is best-effort (exportAsBase64 may be unavailable
    // in older Office builds) — don't surface as a user-facing error, but do
    // report it as a soft/degraded-feature event (not error.connector.failure,
    // which is reserved for failures that actually abort the apply).
    logEvent("connector.action.invoke", "warn", {
      connectorSystemName: APPLY_BRAND_CONNECTOR,
      reason: "brand-apply-ooxml-slide",
    });
  }
}

function recordOoxmlSlideActions(result: ApplyBrandResult, slideNum: number, stats: OoxmlStats): void {
  if (stats.tables > 0 || stats.tableHeaders > 0) {
    result.actions.push(`Slide ${slideNum}: styled ${stats.tableHeaders} table header(s)`);
  }
  if (stats.shadows > 0) result.actions.push(`Slide ${slideNum}: removed ${stats.shadows} shadow(s)`);
  if (stats.gradients > 0) result.actions.push(`Slide ${slideNum}: flattened ${stats.gradients} gradient(s)`);
  if (stats.animations > 0) result.actions.push(`Slide ${slideNum}: removed ${stats.animations} animation(s)`);
}

// ─── Current slide scope ──────────────────────────────────────────────────────

async function applyToCurrentSlide(
  brand: BrandSpec,
  options: Partial<ApplyBrandOptions>,
): Promise<SlideProcessStats & { slideIndex: number }> {
  let stats: SlideProcessStats = { shapesUpdated: 0, aligned: 0, autofit: 0 };
  let slideIndex = 0;
  await PowerPoint.run(async (context) => {
    const slide = await resolveCurrentSlide(context);
    slide.load("index");
    await context.sync();
    slideIndex = slide.index;
    stats = await processSlide(context, slide, brand, null, options);
  });
  return { ...stats, slideIndex };
}

/**
 * Resolve the slide to operate on: the selected slide, falling back to the
 * first slide when nothing is selected (getSelectedSlides can either throw
 * or return an empty collection depending on focus/host). Throws rather than
 * returning undefined so callers can't silently no-op on a missing slide —
 * the failure surfaces as a proper result error instead.
 */
async function resolveCurrentSlide(context: PowerPoint.RequestContext): Promise<PowerPoint.Slide> {
  let slide: PowerPoint.Slide | undefined;
  try {
    const selected = context.presentation.getSelectedSlides();
    selected.load("items");
    await context.sync();
    slide = selected.items[0];
  } catch (e) {
    logOfficeError(e, "brand-apply-get-selected-slides");
  }
  if (!slide) {
    const slides = context.presentation.slides;
    slides.load("items");
    await context.sync();
    slide = slides.items[0];
  }
  if (!slide) throw new Error("No slide available to apply brand to");
  return slide;
}

// ─── Core slide processor ─────────────────────────────────────────────────────

interface ShapeEntry {
  shape: PowerPoint.Shape;
  name: string;
  type: string;
  left: number;
  top: number;
  width: number;
  height: number;
  hasText: boolean;
  fontSize: number | null;
  origFontColor: string | null;
  fillHex: string | null;
  rawFillHex: string | null;
  fillType: string | null;
  fillTransparency: number;
  supportsFill: boolean;
  supportsText: boolean;
  supportsLine: boolean;
  lineVisible: boolean;
  lineColorHex: string | null;
  isTable: boolean;
}

/** Per-slide tallies rolled up into ApplyBrandResult.actions by the callers. */
interface SlideProcessStats {
  shapesUpdated: number;
  aligned: number;
  autofit: number;
}

async function processSlide(
  context: PowerPoint.RequestContext,
  slide: PowerPoint.Slide,
  brand: BrandSpec,
  _aiClassMap: Record<string, string> | null,
  options: Partial<ApplyBrandOptions>,
): Promise<SlideProcessStats> {
  // Phase 1: load shapes
  const shapes = slide.shapes;
  shapes.load("items");
  await context.sync();
  if (shapes.items.length === 0) return { shapesUpdated: 0, aligned: 0, autofit: 0 };

  // Phase 2: load basic props
  for (const shape of shapes.items) shape.load("id,name,type,left,top,width,height");
  await context.sync();

  // Phase 3: build shapeData + queue fill/textFrame loads
  const shapeData = buildShapeEntries(shapes.items);

  // A single invalid load (e.g. a shape type that unexpectedly has no textFrame
  // or fill) rejects the ENTIRE batch sync. Fall back to per-shape isolated
  // syncs so one bad shape only disables itself, not the whole slide.
  try { await context.sync(); } catch (e) {
    logOfficeError(e, "brand-apply-phase3-batch-sync");
    await retryCapabilityLoadsPerShape(context, shapeData);
  }

  // Phase 4a: read fill type; queue colour loads only where known-safe
  queueColorLoads(shapeData);
  await syncQuiet(context);

  // Phase 4b: read foregroundColor + transparency for solid fills, line colour
  // for visible outlines
  readColors(shapeData);

  // Phase 5: load font props
  queueFontLoads(shapeData);
  await syncQuiet(context);

  // Phase 6: capture font sizes + original colours
  captureFontProps(shapeData);

  const titleId = findTitleId(shapeData);
  // Build highlight colour map (body shapes only)
  const highlightMap = buildHighlightColorMap(shapeData, brand, titleId);

  // Repositioning body content ("Fix alignment") is skipped entirely on
  // slides with more than one body-classified shape (e.g. two-column
  // layouts) to avoid collapsing distinct content areas onto the same rect.
  const bodyShapeCount = shapeData.filter((d) => resolveClassification(d, titleId) === "body").length;
  const isSingleBodyShape = bodyShapeCount === 1;
  const slideCtx: SlideBrandContext = { brand, titleId, highlightMap, options, isSingleBodyShape };

  // Phase 7: apply brand per shape
  let shapesUpdated = 0;
  let aligned = 0;
  let autofit = 0;
  for (let idx = 0; idx < shapeData.length; idx++) {
    const r = await applyBrandToShapeEntry(context, shapeData[idx]!, idx, shapeData, slideCtx);
    shapesUpdated += r.updated;
    aligned += r.aligned;
    autofit += r.autofit;
  }

  // Phase 8: flush queued fill changes from Phase 7
  await syncQuiet(context);

  // Phase 9: visibility verification pass
  await verifyTextVisibility(context, shapeData, brand);

  return { shapesUpdated, aligned, autofit };
}

// Phase 3: use the NO_TEXTFRAME_TYPES blacklist — every shape NOT in that list
// gets textFrame loading attempted. Safer than the old whitelist which silently
// skipped many valid shapes (custom geometry, unsupported types that still
// render text, etc.). Groups are handled separately in Phase 7 via recursion.
function buildShapeEntries(shapes: PowerPoint.Shape[]): ShapeEntry[] {
  const shapeData: ShapeEntry[] = [];
  for (const shape of shapes) {
    const shapeType = String(shape.type ?? "");
    const isTextShape = !NO_TEXTFRAME_TYPES.has(shapeType) && !GROUP_TYPES.has(shapeType) && !SPECIAL_TEXT_TYPES.has(shapeType);
    const hasFill = !NO_FILL_TYPES.has(shapeType) && !GROUP_TYPES.has(shapeType);
    const hasLine = !NO_LINE_TYPES.has(shapeType) && !GROUP_TYPES.has(shapeType);
    const entry: ShapeEntry = {
      shape, name: shape.name ?? "", type: shapeType,
      left: shape.left, top: shape.top, width: shape.width, height: shape.height,
      hasText: false, fontSize: null, origFontColor: null,
      fillHex: null, rawFillHex: null, fillType: null, fillTransparency: 0,
      supportsFill: false, supportsText: false,
      supportsLine: false, lineVisible: false, lineColorHex: null,
      isTable: shapeType === "Table" || shapeType === "table",
    };
    // Phase 3 loads ONLY "type" for fills — loading foregroundColor on non-solid
    // fills (gradient, pattern, noFill) causes E_INVALIDARG (0x80070057) which
    // fails the whole batch sync. We read foregroundColor in Phase 4b once we
    // know the fill type. Same staging for lines: "visible" first, colour later.
    entry.supportsFill = hasFill && tryQueue(() => shape.fill.load("type"));
    entry.supportsText = isTextShape && tryQueue(() => shape.textFrame.load("hasText"));
    entry.supportsLine = hasLine && tryQueue(() => shape.lineFormat.load("visible"));
    shapeData.push(entry);
  }
  return shapeData;
}

// Phase 3 fallback: re-attempt each wanted capability with its own isolated
// sync so one bad shape only disables itself.
async function retryCapabilityLoadsPerShape(context: PowerPoint.RequestContext, shapeData: ShapeEntry[]): Promise<void> {
  for (const d of shapeData) {
    const wantFill = d.supportsFill;
    const wantText = d.supportsText;
    const wantLine = d.supportsLine;
    d.supportsFill = wantFill && await trySyncLoad(context, () => d.shape.fill.load("type"));
    d.supportsText = wantText && await trySyncLoad(context, () => d.shape.textFrame.load("hasText"));
    d.supportsLine = wantLine && await trySyncLoad(context, () => d.shape.lineFormat.load("visible"));
  }
}

// Phase 4a: read fill type; queue foregroundColor+transparency only for solid
// fills. Same staging for lines: queue "color" only for visible lines —
// loading color on a shape with no outline can fail the batch.
function queueColorLoads(shapeData: ShapeEntry[]): void {
  for (const d of shapeData) {
    if (d.supportsFill) {
      d.fillType = tryGet(() => String(d.shape.fill.type ?? "").toLowerCase());
      if (d.fillType === "solid") tryQueue(() => d.shape.fill.load("foregroundColor,transparency"));
    }
    if (d.supportsText) d.hasText = tryGet(() => d.shape.textFrame.hasText) ?? false;
    if (d.supportsLine) {
      d.lineVisible = tryGet(() => d.shape.lineFormat.visible === true) ?? false;
      if (d.lineVisible) tryQueue(() => d.shape.lineFormat.load("color"));
    }
  }
}

// Phase 4b
function readColors(shapeData: ShapeEntry[]): void {
  for (const d of shapeData) {
    if (d.supportsFill && d.fillType === "solid") readSolidFillColor(d);
    if (d.supportsLine && d.lineVisible) readLineColor(d);
  }
}

function readSolidFillColor(d: ShapeEntry): void {
  try {
    const rawFill = (d.shape.fill.foregroundColor ?? "").replace("#", "");
    d.rawFillHex = rawFill;
    const trans = tryGet(() => d.shape.fill.transparency) || 0;
    d.fillTransparency = trans;
    d.fillHex = rawFill.length === 6 && trans > 0 ? blendWithWhite(rawFill, trans) : rawFill;
  } catch (e) {
    // foregroundColor can still throw here even though Phase 4a already saw
    // fillType === "solid": the proxy read is a separate round-trip, and a
    // shape whose fill was concurrently edited (or an Office.js quirk on
    // certain placeholder fills) can flip/error between the two. Leave
    // rawFillHex/fillHex unset so the fill remap simply skips this shape
    // instead of acting on partial data.
    logOfficeError(e, "brand-apply-shape-fill-read");
  }
}

function readLineColor(d: ShapeEntry): void {
  const rawLine = tryGet(() => (d.shape.lineFormat.color ?? "").replace("#", ""));
  if (rawLine) d.lineColorHex = rawLine;
}

// Phase 5
function queueFontLoads(shapeData: ShapeEntry[]): void {
  for (const d of shapeData) {
    if (d.hasText && d.supportsText) tryQueue(() => d.shape.textFrame.textRange.font.load("size,color,bold,name"));
  }
}

// Phase 6
function captureFontProps(shapeData: ShapeEntry[]): void {
  for (const d of shapeData) {
    if (!d.hasText || !d.supportsText) continue;
    d.fontSize = tryGet(() => d.shape.textFrame.textRange.font.size) ?? null;
    const fc = tryGet(() => d.shape.textFrame.textRange.font.color);
    if (fc) d.origFontColor = normalizeHex(String(fc).replace("#", ""));
  }
}

// Build the per-slide title proxy and resolve the title shape id
function findTitleId(shapeData: ShapeEntry[]): string | null {
  const shapeProxies = shapeData.map((d) => ({
    id: d.shape.id ?? "", name: d.name, type: d.type,
    left: d.left, top: d.top, width: d.width, height: d.height,
  }));
  return findTitleShape(shapeProxies)?.id ?? null;
}

/** Per-shape outcome of applyBrandToShapeEntry — rolled up by processSlide. */
interface ShapeEntryResult {
  updated: number;
  aligned: number;
  autofit: number;
}

const NO_SHAPE_ENTRY_RESULT: ShapeEntryResult = { updated: 0, aligned: 0, autofit: 0 };

/** The per-slide inputs to applyBrandToShapeEntry — computed once in
 * processSlide (Phases 1-6) and shared, unchanged, across every shape in
 * that slide's Phase 7 loop. */
interface SlideBrandContext {
  brand: BrandSpec;
  titleId: string | null;
  highlightMap: Map<string, string>;
  options: Partial<ApplyBrandOptions>;
  isSingleBodyShape: boolean;
}

// Phase 7: apply brand to a single shape entry; `updated` is how many shapes
// were text-updated (0 or 1 directly, more via group recursion — group
// children don't currently participate in alignment/autofit, see
// applyBrandToGroupEntry).
async function applyBrandToShapeEntry(
  context: PowerPoint.RequestContext,
  d: ShapeEntry,
  idx: number,
  shapeData: ShapeEntry[],
  slideCtx: SlideBrandContext,
): Promise<ShapeEntryResult> {
  const { brand, titleId, highlightMap, options, isSingleBodyShape } = slideCtx;
  try {
    // Tables are styled by the OOXML post-processing pass (their shape-level
    // fill/text APIs are not usable via Office.js).
    if (d.isTable) return NO_SHAPE_ENTRY_RESULT;
    if (GROUP_TYPES.has(d.type)) {
      return { ...NO_SHAPE_ENTRY_RESULT, updated: await applyBrandToGroupEntry(context, d, brand) };
    }

    // Border/line colour: remap any visible outline to the brand palette.
    // Covers shape borders AND standalone lines/connectors.
    if (d.supportsLine && d.lineVisible && d.lineColorHex) {
      applyMappedLineColor(d.shape, d.lineColorHex, brand);
    }

    if (NO_TEXTFRAME_TYPES.has(d.type)) return { ...NO_SHAPE_ENTRY_RESULT, updated: applyFillOnly(d, brand) };

    const cls = resolveClassification(d, titleId);
    const activeFill = remapFillAndGetActive(d, idx, shapeData, brand);

    if (!d.hasText || !d.supportsText) return NO_SHAPE_ENTRY_RESULT;
    await applyClassFormat(context, d.shape, cls, brand, activeFill, d.origFontColor, highlightMap);
    const { aligned, autofit } = await applyLayoutAdjustments(context, d.shape, cls, brand, options, isSingleBodyShape);
    return { updated: 1, aligned: aligned ? 1 : 0, autofit: autofit ? 1 : 0 };
  } catch (e) {
    logOfficeError(e, "brand-apply-shape");
    return NO_SHAPE_ENTRY_RESULT;
  }
}

// ─── Layout adjustments ("Fix alignment" / "Autofit slide content") ─────────

/**
 * "Fix alignment" repositions title/footnote/body shapes to the brand's
 * extracted placeholder geometry; "Autofit slide content" turns on
 * shrink-text-on-overflow. Both are opt-in and best-effort (try/catch,
 * matching every other phase in this file) — a failure here never blocks the
 * rest of the brand apply.
 */
async function applyLayoutAdjustments(
  context: PowerPoint.RequestContext,
  shape: PowerPoint.Shape,
  cls: ShapeClassification,
  brand: BrandSpec,
  options: Partial<ApplyBrandOptions>,
  isSingleBodyShape: boolean,
): Promise<{ aligned: boolean; autofit: boolean }> {
  let aligned = false;
  let autofit = false;

  if (options.fixAlignment) {
    const target = resolveAlignmentTarget(cls, brand, isSingleBodyShape);
    if (target && target.width > 0 && target.height > 0) {
      try {
        shape.left = target.left;
        shape.top = target.top;
        shape.width = target.width;
        shape.height = target.height;
        await context.sync();
        aligned = true;
      } catch (e) {
        logOfficeError(e, "brand-apply-fix-alignment");
      }
    }
  }

  if (options.autofitSlideContent) {
    try {
      shape.textFrame.autoSizeSetting = "AutoSizeTextToFitShape";
      await context.sync();
      autofit = true;
    } catch (e) {
      logOfficeError(e, "brand-apply-autofit");
    }
  }

  return { aligned, autofit };
}

/** The brand's stored placeholder rect for a shape's role, or null when none
 * was extracted (title-only/footnote decks) or the role doesn't reposition
 * (body only repositions when the slide has exactly one body shape). */
function resolveAlignmentTarget(
  cls: ShapeClassification,
  brand: BrandSpec,
  isSingleBodyShape: boolean,
): { left: number; top: number; width: number; height: number } | null {
  if (cls === "title") return brand.titleFormat.position ?? null;
  if (cls === "footnote") return brand.footnoteFormat.position ?? null;
  if (cls === "body" && isSingleBodyShape) return brand.bodyArea;
  return null;
}

async function applyBrandToGroupEntry(context: PowerPoint.RequestContext, d: ShapeEntry, brand: BrandSpec): Promise<number> {
  try {
    return await processGroupChildren(context, d.shape, brand, 0);
  } catch (e) {
    logOfficeError(e, "brand-apply-group");
    return 0;
  }
}

// Non-text shapes (images, lines, media, etc.): remap fill only
function applyFillOnly(d: ShapeEntry, brand: BrandSpec): number {
  if (d.fillHex && d.rawFillHex) applyMappedFill(d.shape, d.rawFillHex, brand, d.fillTransparency);
  return 0;
}

function resolveClassification(d: ShapeEntry, titleId: string | null): ShapeClassification {
  const cls = classifyShape(d, d.fontSize, SLIDE_HEIGHT_PTS);
  if (cls === "title" && titleId && d.shape.id !== titleId) return "body";
  return cls;
}

function isNoFillType(fillType: string | null): boolean {
  return !fillType || fillType === "nofill" || fillType === "noFill" || fillType === "none";
}

// Remap the shape's solid fill to the brand palette and determine the fill
// behind its text: the (possibly remapped) own fill, or for unfilled text
// boxes the dark fill of a covering shape underneath.
function remapFillAndGetActive(d: ShapeEntry, idx: number, shapeData: ShapeEntry[], brand: BrandSpec): string | null {
  let effectiveFill = d.fillHex;
  if (!effectiveFill && d.hasText && isNoFillType(d.fillType)) {
    const underFill = findUnderlyingFill(idx, shapeData);
    if (underFill && luminance(underFill) < MID_LUMINANCE) effectiveFill = underFill;
  }
  const newFill = d.fillType === "solid" && d.rawFillHex
    ? applyMappedFill(d.shape, d.rawFillHex, brand, d.fillTransparency)
    : null;
  return newFill ?? effectiveFill;
}

async function applyClassFormat(
  context: PowerPoint.RequestContext,
  shape: PowerPoint.Shape,
  cls: ShapeClassification,
  brand: BrandSpec,
  fillHex: string | null,
  origFontColor: string | null,
  highlightMap: Map<string, string>,
): Promise<void> {
  switch (cls) {
    case "title":
      await applyTitleFormat(context, shape, brand, fillHex, origFontColor);
      break;
    case "subtitle":
      await applySubtitleFormat(context, shape, brand, fillHex, origFontColor);
      break;
    case "footnote":
      await applyFootnoteFormat(context, shape, brand, fillHex, origFontColor);
      break;
    default:
      await applyBodyFormat(context, shape, brand, fillHex, origFontColor, highlightMap);
  }
}

// ─── Group children processing ───────────────────────────────────────────────

const MAX_GROUP_DEPTH = 5;

async function processGroupChildren(
  context: PowerPoint.RequestContext,
  groupShape: PowerPoint.Shape,
  brand: BrandSpec,
  depth: number,
): Promise<number> {
  if (depth >= MAX_GROUP_DEPTH) return 0;

  // Access group.shapes directly — "group" is a navigation property and cannot
  // be loaded via shape.load("group") like a scalar (causes InvalidArgument).
  const children = groupShape.group.shapes;
  children.load("items");
  await context.sync();
  if (!children.items.length) return 0;

  let childShapesUpdated = 0;

  for (const child of children.items) child.load("id,name,type,left,top,width,height");
  await context.sync();

  for (const child of children.items) {
    try {
      childShapesUpdated += await processGroupChild(context, child, brand, depth);
    } catch (e) {
      logOfficeError(e, "brand-apply-group-child-outer");
    }
  }

  return childShapesUpdated;
}

async function processGroupChild(
  context: PowerPoint.RequestContext,
  child: PowerPoint.Shape,
  brand: BrandSpec,
  depth: number,
): Promise<number> {
  const childType = String(child.type ?? "");
  if (GROUP_TYPES.has(childType)) return processGroupChildren(context, child, brand, depth + 1);

  const { hasFill, hasText } = await loadChildCapabilities(context, child, childType);
  const fillHex = await remapChildFill(context, child, brand, hasFill);
  await remapChildLine(context, child, childType, brand);

  if (hasText && child.textFrame.hasText) return applyChildTextFormat(context, child, brand, fillHex);
  return 0;
}

// Tables/charts inside groups have no usable textFrame either — treat them
// (and NO_TEXTFRAME types) as fill-only so a bad textFrame load can't fail
// the sync and knock out the fill remap too. On batch failure retry each
// capability separately so one invalid load only disables itself, not both.
async function loadChildCapabilities(
  context: PowerPoint.RequestContext,
  child: PowerPoint.Shape,
  childType: string,
): Promise<{ hasFill: boolean; hasText: boolean }> {
  const textCapable = !NO_TEXTFRAME_TYPES.has(childType) && !SPECIAL_TEXT_TYPES.has(childType);
  const fillCapable = !NO_FILL_TYPES.has(childType);
  if (!fillCapable && !textCapable) return { hasFill: false, hasText: false };

  try {
    if (fillCapable) child.fill.load("type");
    if (textCapable) child.textFrame.load("hasText");
    await context.sync();
    return { hasFill: fillCapable, hasText: textCapable };
  } catch {
    return {
      hasFill: fillCapable && await trySyncLoad(context, () => child.fill.load("type")),
      hasText: textCapable && await trySyncLoad(context, () => child.textFrame.load("hasText")),
    };
  }
}

async function remapChildFill(
  context: PowerPoint.RequestContext,
  child: PowerPoint.Shape,
  brand: BrandSpec,
  hasFill: boolean,
): Promise<string | null> {
  if (!hasFill || child.fill?.type?.toString().toLowerCase() !== "solid") return null;
  try {
    child.fill.load("foregroundColor,transparency");
    await context.sync();
    const rawHex = (child.fill.foregroundColor ?? "").replace("#", "");
    const trans = child.fill.transparency ?? 0;
    const fillHex = rawHex.length === 6 && trans > 0 ? blendWithWhite(rawHex, trans) : rawHex;
    if (rawHex) applyMappedFill(child, rawHex, brand, trans);
    return fillHex;
  } catch { return null; }
}

// Border/line colour — same staged load as the top-level pass (visible first,
// colour only when visible) inside an isolated sync.
async function remapChildLine(
  context: PowerPoint.RequestContext,
  child: PowerPoint.Shape,
  childType: string,
  brand: BrandSpec,
): Promise<void> {
  if (NO_LINE_TYPES.has(childType)) return;
  try {
    child.lineFormat.load("visible");
    await context.sync();
    if (child.lineFormat.visible !== true) return;
    child.lineFormat.load("color");
    await context.sync();
    const rawLine = (child.lineFormat.color ?? "").replace("#", "");
    if (rawLine) {
      applyMappedLineColor(child, rawLine, brand);
      await context.sync();
    }
  } catch { /* ignore */ }
}

async function applyChildTextFormat(
  context: PowerPoint.RequestContext,
  child: PowerPoint.Shape,
  brand: BrandSpec,
  fillHex: string | null,
): Promise<number> {
  try {
    child.textFrame.textRange.font.load("size,color");
    await context.sync();
    const fontSize = child.textFrame.textRange.font.size;
    const origFontColor = normalizeHex(String(child.textFrame.textRange.font.color ?? "").replace("#", ""));
    const cls = classifyShape(child, fontSize, SLIDE_HEIGHT_PTS);
    await applyClassFormat(context, child, cls, brand, fillHex, origFontColor, new Map<string, string>());
    return 1;
  } catch (e) {
    logOfficeError(e, "brand-apply-group-child-text");
    return 0;
  }
}

// ─── Format appliers ──────────────────────────────────────────────────────────

function fillAwareColor(
  intendedColor: string | null | undefined,
  fillHex: string | null | undefined,
  brand: BrandSpec,
  origFontColor: string | null,
): string | null {
  const lt1 = normalizeHex(brand.colors.lt1) ?? "FFFFFF";
  const fillNorm = normalizeHex(fillHex ?? "");
  const fillLum = fillNorm ? luminance(fillNorm) : null;

  if (origFontColor) {
    const origNorm = normalizeHex(origFontColor);
    if (origNorm && luminance(origNorm) > ORIGINAL_FONT_NEAR_WHITE_LUMINANCE) {
      if (fillLum === null || fillLum < MID_LUMINANCE) return `#${lt1}`;
    }
  }
  if (!fillNorm) return intendedColor ?? null;
  if (fillLum !== null && fillLum < MID_LUMINANCE) return `#${lt1}`;
  return intendedColor ?? null;
}

async function applyTitleFormat(
  context: PowerPoint.RequestContext,
  shape: PowerPoint.Shape,
  brand: BrandSpec,
  fillHex: string | null | undefined,
  origFontColor: string | null,
): Promise<void> {
  const fmt = brand.titleFormat;
  try {
    const textColor = fillAwareColor(fmt.fontColor ? `#${normalizeHex(fmt.fontColor) ?? fmt.fontColor}` : null, fillHex, brand, origFontColor);
    await applyFontToTextFrame(context, shape.textFrame, fmt.fontName, textColor, fmt.bold ?? true, fmt.fontSize);
  } catch { /* ignore */ }
}

async function applySubtitleFormat(
  context: PowerPoint.RequestContext,
  shape: PowerPoint.Shape,
  brand: BrandSpec,
  fillHex: string | null | undefined,
  origFontColor: string | null,
): Promise<void> {
  const fmt = brand.subtitleFormat ?? {
    fontName: brand.bodyTextFormat.fontName,
    fontSize: (brand.bodyTextFormat.fontSize ?? 14) + 4,
    bold: false,
    fontColor: brand.colors.dk2 ?? brand.colors.dk1,
  };
  try {
    const textColor = fillAwareColor(fmt.fontColor ? `#${normalizeHex(fmt.fontColor) ?? fmt.fontColor}` : null, fillHex, brand, origFontColor);
    await applyFontToTextFrame(context, shape.textFrame, fmt.fontName, textColor, fmt.bold ?? false, fmt.fontSize);
  } catch { /* ignore */ }
}

async function applyBodyFormat(
  context: PowerPoint.RequestContext,
  shape: PowerPoint.Shape,
  brand: BrandSpec,
  fillHex: string | null | undefined,
  origFontColor: string | null,
  highlightMap: Map<string, string>,
): Promise<void> {
  const fmt = brand.bodyTextFormat;
  try {
    // Mixed-colour frames (origFontColor === null) keep their per-run colours —
    // recolouring the whole frame would wipe intentional highlights. The OOXML
    // pass still snaps any non-neutral run colours to the brand palette, and
    // verifyTextVisibility repairs unreadable combinations.
    let textColor: string | null = null;
    if (origFontColor) {
      const intended = resolveIntendedColor(origFontColor, fmt.fontColor, brand, highlightMap);
      textColor = fillAwareColor(intended, fillHex, brand, origFontColor);
    }
    await applyFontToTextFrame(context, shape.textFrame, fmt.fontName, textColor, undefined, undefined);
  } catch { /* ignore */ }
}

async function applyFootnoteFormat(
  context: PowerPoint.RequestContext,
  shape: PowerPoint.Shape,
  brand: BrandSpec,
  fillHex: string | null | undefined,
  origFontColor: string | null,
): Promise<void> {
  const fmt = brand.footnoteFormat;
  try {
    const textColor = fillAwareColor(fmt.fontColor ? `#${normalizeHex(fmt.fontColor) ?? fmt.fontColor}` : null, fillHex, brand, origFontColor);
    await applyFontToTextFrame(context, shape.textFrame, fmt.fontName, textColor, undefined, fmt.fontSize);
  } catch { /* ignore */ }
}

async function applyFontToTextFrame(
  context: PowerPoint.RequestContext,
  textFrame: PowerPoint.TextFrame,
  fontName: string | null | undefined,
  fontColor: string | null | undefined,
  bold: boolean | undefined,
  fontSize: number | null | undefined,
): Promise<void> {
  try {
    const tr = textFrame.textRange;
    if (fontName) { const n = fontName.trim(); if (n.length > 0 && !n.startsWith("+")) tr.font.name = n; }
    if (fontSize && typeof fontSize === "number" && fontSize > 0) tr.font.size = fontSize;
    if (fontColor) { const h = normalizeHex(fontColor.replace("#", "")); if (h) tr.font.color = `#${h}`; }
    if (bold !== undefined && bold !== null) tr.font.bold = bold;
    await context.sync();
  } catch { /* ignore */ }
}

// ─── Fill mapping ─────────────────────────────────────────────────────────────

function applyMappedFill(
  shape: PowerPoint.Shape,
  fillHex: string,
  brand: BrandSpec,
  transparency: number,
): string | null {
  const norm = normalizeHex(fillHex);
  if (!norm) return null;
  if (isNearWhite(norm, APPLY_BRAND_NEAR_WHITE_THRESHOLD) || isNearBlack(norm)) return null;
  try {
    const curType = String(shape.fill?.type ?? "").toLowerCase();
    if (curType && curType !== "solid") return null;
  } catch { return null; }
  let transPct = 0;
  if (transparency > 0) transPct = transparency <= 1 ? transparency * 100 : transparency;
  if (transPct > 50) return null;

  const candidates = [
    brand.colors.accent1, brand.colors.accent2, brand.colors.accent3,
    brand.colors.accent4, brand.colors.accent5, brand.colors.accent6,
    brand.colors.dk1, brand.colors.dk2,
  ];
  const mapped = findNearestBrandColor(norm, candidates);
  if (mapped) {
    try {
      shape.fill.setSolidColor(mapped);
      if (transparency > 0) shape.fill.transparency = transparency;
    } catch { /* ignore */ }
    return normalizeHex(mapped);
  }
  return null;
}

/**
 * Remap a shape outline (or standalone line/connector) to the brand palette:
 *  - near-white / near-black lines are left alone (deliberate contrast)
 *  - neutral greys snap to the brand line colour
 *  - saturated colours snap to the nearest brand accent/dark colour
 */
function applyMappedLineColor(
  shape: PowerPoint.Shape,
  lineHex: string,
  brand: BrandSpec,
): string | null {
  const norm = normalizeHex(lineHex);
  if (!norm) return null;
  if (isNearWhite(norm, APPLY_BRAND_NEAR_WHITE_THRESHOLD) || isNearBlack(norm)) return null;

  let target: string | null;
  if (isNeutralHex(norm)) {
    target = normalizeHex(brand.lineColor);
  } else {
    const candidates = [
      brand.colors.accent1, brand.colors.accent2, brand.colors.accent3,
      brand.colors.accent4, brand.colors.accent5, brand.colors.accent6,
      brand.colors.dk1, brand.colors.dk2,
    ];
    target = normalizeHex(findNearestBrandColor(norm, candidates) ?? "");
  }
  if (!target || target === norm) return null;

  try { shape.lineFormat.color = `#${target}`; } catch { return null; }
  return target;
}

// ─── Highlight colour map ─────────────────────────────────────────────────────

function collectBodyHighlightColors(shapeData: ShapeEntry[]): Set<string> {
  const bodyHighlights = new Set<string>();
  for (const d of shapeData) {
    if (!d.hasText || !d.supportsText || !d.origFontColor) continue;
    if (isDefaultFontColor(d.origFontColor)) continue;
    const cls = classifyShape(d, d.fontSize, SLIDE_HEIGHT_PTS);
    if (cls === "title" || cls === "subtitle" || cls === "footnote") continue;
    const k = normalizeHex(d.origFontColor);
    if (k) bodyHighlights.add(k.toUpperCase());
  }
  return bodyHighlights;
}

function buildHighlightColorMap(
  shapeData: ShapeEntry[],
  brand: BrandSpec,
  _titleId: string | null,
): Map<string, string> {
  const bodyHighlights = collectBodyHighlightColors(shapeData);
  const map = new Map<string, string>();
  if (bodyHighlights.size === 0) return map;

  const accents = [
    brand.colors.accent1, brand.colors.accent2, brand.colors.accent3,
    brand.colors.accent4, brand.colors.accent5, brand.colors.accent6,
  ].map((c) => normalizeHex(c)).filter((c): c is string => c !== null);

  const highlightCol = normalizeHex(brand.highlightColor) ?? accents[0] ?? "4472C4";

  if (bodyHighlights.size === 1) {
    for (const h of bodyHighlights) map.set(h, highlightCol);
  } else {
    let i = 0;
    for (const h of bodyHighlights) {
      map.set(h, accents[i % Math.max(accents.length, 1)] ?? highlightCol);
      i++;
    }
  }
  return map;
}

function resolveIntendedColor(
  origFontColor: string | null,
  defaultColor: string,
  brand: BrandSpec,
  highlightMap: Map<string, string>,
): string {
  if (origFontColor && !isDefaultFontColor(origFontColor)) {
    const key = normalizeHex(origFontColor)?.toUpperCase();
    if (key) {
      const mapped = highlightMap.get(key);
      if (mapped) return mapped;
    }
    return normalizeHex(brand.highlightColor) ?? normalizeHex(brand.colors.accent1) ?? defaultColor;
  }
  return defaultColor;
}

// ─── Shape classification ─────────────────────────────────────────────────────

interface ClassifiableShape { name: string; type: string; top: number; width: number; height: number; }

function classifyShape(shape: ClassifiableShape, fontSize: number | null, slideH: number): ShapeClassification {
  const t = String(shape.type ?? "");
  const byType = classifyByType(t);
  if (byType) return byType;

  const byName = classifyByName((shape.name ?? "").toLowerCase());
  if (byName) return byName;

  return classifyByGeometry(shape, t, fontSize, slideH);
}

function classifyByType(t: string): ShapeClassification | null {
  if (t === "Table" || t === "table") return "table";
  if (t === "Chart" || t === "chart") return "chart";
  if (t === "Image" || t === "image" || t === "Picture" || t === "picture") return "image";
  return null;
}

const FOOTNOTE_NAME_HINTS = ["footer", "ftr", "footnote", "source", "note", "disclaimer", "slide number", "date"];
const BODY_NAME_HINTS = ["content", "body", "text placeholder"];

function classifyByName(n: string): ShapeClassification | null {
  if (n.includes("title") && !n.includes("subtitle")) return "title";
  if (n.includes("subtitle")) return "subtitle";
  if (FOOTNOTE_NAME_HINTS.some((hint) => n.includes(hint))) return "footnote";
  if (BODY_NAME_HINTS.some((hint) => n.includes(hint))) return "body";
  return null;
}

function classifyByGeometry(shape: ClassifiableShape, t: string, fontSize: number | null, slideH: number): ShapeClassification {
  if (shape.width < 20 || shape.height < 20) return "decorator";

  const relBottom = (shape.top + shape.height) / (slideH || SLIDE_HEIGHT_PTS);
  const relTop = shape.top / (slideH || SLIDE_HEIGHT_PTS);

  if (fontSize && fontSize <= 9) return "footnote";
  if (relBottom > 0.80 && fontSize && fontSize <= 11) return "footnote";
  if (relTop < 0.25 && fontSize && fontSize > 20) return "title";
  if (fontSize && fontSize >= 10 && fontSize <= 20) return "body";
  if (t === "GeometricShape" || t === "geometricShape") return fontSize ? "body" : "decorator";

  return "unknown";
}

// ─── Title shape finder ────────────────────────────────────────────────────────

interface ShapeProxy { id: string; name: string; type: string; left: number; top: number; width: number; height: number; }

function findTitleShape(shapes: ShapeProxy[]): ShapeProxy | null {
  // 1. Explicit name match
  const byName = shapes.find((s) => s.name.toLowerCase().includes("title") && !s.name.toLowerCase().includes("subtitle"));
  if (byName) return byName;
  // 2. Largest text shape in top 30%
  const [first, ...rest] = shapes.filter((s) => (s.top + s.height / 2) / SLIDE_HEIGHT_PTS < 0.3);
  if (!first) return null;
  return rest.reduce((best, s) => s.width * s.height > best.width * best.height ? s : best, first);
}

// ─── Overlap / z-order ────────────────────────────────────────────────────────

function overlapRatio(a: { left: number; top: number; width: number; height: number }, b: { left: number; top: number; width: number; height: number }): number {
  const ol = Math.max(a.left, b.left), ot = Math.max(a.top, b.top);
  const or2 = Math.min(a.left + a.width, b.left + b.width);
  const ob = Math.min(a.top + a.height, b.top + b.height);
  if (or2 <= ol || ob <= ot) return 0;
  return ((or2 - ol) * (ob - ot)) / (a.width * a.height || 1);
}

function findUnderlyingFill(targetIdx: number, shapeData: ShapeEntry[]): string | null {
  const target = shapeData[targetIdx]!;
  for (let i = targetIdx - 1; i >= 0; i--) {
    const other = shapeData[i]!;
    if (!other.fillHex) continue;
    if (overlapRatio(target, other) >= OVERLAP_COVERAGE_THRESHOLD) return other.fillHex;
  }
  return null;
}

// ─── Visibility verification ──────────────────────────────────────────────────

async function verifyTextVisibility(
  context: PowerPoint.RequestContext,
  shapeData: ShapeEntry[],
  brand: BrandSpec,
): Promise<void> {
  queueVisibilityTypeLoads(shapeData);
  await syncQuiet(context);

  queueVisibilityColorLoads(shapeData);
  await syncQuiet(context);

  repairUnreadableText(shapeData, brand);
  await syncQuiet(context);
}

// Load fill type first, then foregroundColor only for solid fills — loading
// foregroundColor on non-solid fills throws E_INVALIDARG and fails the batch.
function queueVisibilityTypeLoads(shapeData: ShapeEntry[]): void {
  for (const d of shapeData) {
    if (!d.supportsText || !d.hasText) continue;
    if (d.supportsFill) tryQueue(() => d.shape.fill.load("type"));
    tryQueue(() => d.shape.textFrame.textRange.font.load("color"));
  }
}

function queueVisibilityColorLoads(shapeData: ShapeEntry[]): void {
  for (const d of shapeData) {
    if (!d.supportsText || !d.hasText || !d.supportsFill) continue;
    tryQueue(() => {
      if (String(d.shape.fill.type ?? "").toLowerCase() === "solid") {
        d.shape.fill.load("foregroundColor");
      }
    });
  }
}

function repairUnreadableText(shapeData: ShapeEntry[], brand: BrandSpec): void {
  const lt1 = normalizeHex(brand.colors.lt1) ?? "FFFFFF";
  const dk1 = normalizeHex(brand.colors.dk1) ?? "000000";
  for (const d of shapeData) {
    if (!d.supportsText || !d.hasText) continue;
    repairShapeTextColor(d, shapeData, lt1, dk1);
  }
}

function repairShapeTextColor(d: ShapeEntry, shapeData: ShapeEntry[], lt1: string, dk1: string): void {
  const effectiveFill = getEffectiveFillForVisibility(d, shapeData);
  if (!effectiveFill) return;

  const currentText = tryGet(() => {
    const c = d.shape.textFrame.textRange.font.color;
    return c ? normalizeHex(String(c).replace("#", "")) : null;
  });
  if (!currentText || !isTextUnreadable(currentText, effectiveFill)) return;

  const desired = luminance(effectiveFill) < MID_LUMINANCE ? lt1 : dk1;
  if (desired !== currentText) {
    tryQueue(() => { d.shape.textFrame.textRange.font.color = `#${desired}`; });
  }
}

function getEffectiveFillForVisibility(d: ShapeEntry, shapeData: ShapeEntry[]): string | null {
  const solidFill = tryGet(() => {
    const t = String(d.shape.fill.type ?? "").toLowerCase();
    return t === "solid" ? normalizeHex((d.shape.fill.foregroundColor ?? "").replace("#", "")) : null;
  });
  return solidFill ?? findUnderlyingFill(shapeData.indexOf(d), shapeData);
}

// ─── OOXML post-processing ────────────────────────────────────────────────────

interface OoxmlStats {
  shadows: number; gradients: number; corners: number; animations: number;
  tables: number; tableHeaders: number; bullets: number;
  imagesCompressed: number; bytesSaved: number; layoutsSwapped: number;
}

function emptyOoxmlStats(): OoxmlStats {
  return { shadows: 0, gradients: 0, corners: 0, animations: 0, tables: 0, tableHeaders: 0, bullets: 0, imagesCompressed: 0, bytesSaved: 0, layoutsSwapped: 0 };
}

async function applyOoxmlViaExport(
  brand: BrandSpec,
  _scope: "slide" | "deck",
  options: Partial<ApplyBrandOptions>,
): Promise<OoxmlStats> {
  const stats = emptyOoxmlStats();
  await PowerPoint.run(async (context) => {
    const slide = await resolveCurrentSlide(context);
    const runState: { titleOnlySwapDone: boolean } = { titleOnlySwapDone: false };
    const s = await applyOoxmlToSlide(context, slide, brand, options, runState);
    Object.assign(stats, s);
  });
  return stats;
}

interface OoxmlPassConfig {
  bodyFont: string | undefined;
  preserveFonts: string[];
  hasTableFormat: boolean;
  hasColors: boolean;
  removeShadows: boolean;
  removeGradients: boolean;
  removeCorners: boolean;
  removeAnimations: boolean;
}

function buildOoxmlPassConfig(brand: BrandSpec, options: Partial<ApplyBrandOptions>): OoxmlPassConfig {
  return {
    bodyFont: brand.bodyTextFormat?.fontName ?? brand.fonts?.bodyFont,
    // Fonts the OOXML rewrite must NOT touch (title font set by the Office.js pass)
    preserveFonts: [brand.titleFormat?.fontName, brand.fonts?.titleFont, brand.subtitleFormat?.fontName]
      .filter((f): f is string => !!f),
    hasTableFormat: !!(brand.tableFormat?.headerRow),
    hasColors: !!(brand.colors),
    removeShadows: options.allowShadows === false,
    removeGradients: options.allowGradients === false,
    removeCorners: options.allowRoundedCorners === false,
    removeAnimations: options.allowAnimations === false,
  };
}

function ooxmlPassHasWork(cfg: OoxmlPassConfig): boolean {
  return !!cfg.bodyFont || cfg.hasTableFormat || cfg.hasColors
    || cfg.removeShadows || cfg.removeGradients || cfg.removeCorners || cfg.removeAnimations;
}

async function applyOoxmlToSlide(
  context: PowerPoint.RequestContext,
  slide: PowerPoint.Slide,
  brand: BrandSpec,
  options: Partial<ApplyBrandOptions>,
  _runState: { titleOnlySwapDone: boolean },
): Promise<OoxmlStats> {
  const stats = emptyOoxmlStats();
  const cfg = buildOoxmlPassConfig(brand, options);
  if (!ooxmlPassHasWork(cfg)) return stats;

  try {
    const exportResult = slide.exportAsBase64();
    slide.load("id");
    await context.sync();
    const slideBase64 = exportResult.value;
    const slideId = slide.id;

    const zip = await unzipPptxBase64(slideBase64);
    const slideXmlBytes = zip["ppt/slides/slide1.xml"];
    if (!slideXmlBytes) return stats;

    const orig = strFromU8(slideXmlBytes);
    const xml = transformSlideXml(orig, brand, cfg, stats);
    if (xml !== orig) zip["ppt/slides/slide1.xml"] = strToU8(xml);

    // ── Charts ──────────────────────────────────────────────────────────────
    // The per-slide export zip includes embedded chart XML files.
    // Snap brand colours and rewrite fonts inside each chart.
    const chartsChanged = await rebrandZipParts(zip, /^ppt\/charts\/chart\d+\.xml$/, brand, cfg);
    stats.tables += chartsChanged;

    // ── SmartArt diagrams ───────────────────────────────────────────────────
    // SmartArt shapes live in separate diagram parts (data/drawing/colors XML),
    // unreachable via Office.js. Snap explicit colours, convert theme colour
    // references to brand colours, and rewrite fonts in each part.
    const diagramsChanged = await rebrandZipParts(zip, /^ppt\/diagrams\/[^/]+\.xml$/, brand, cfg);

    // ── Images ──────────────────────────────────────────────────────────────
    // Compress raster media files >50 KB (opt-in via compressImages option).
    if (options.compressImages) {
      const { saved, count } = await compressMediaImages(zip);
      stats.imagesCompressed = count;
      stats.bytesSaved = saved;
    }

    const anyChange = xml !== orig || chartsChanged > 0 || diagramsChanged > 0 || stats.imagesCompressed > 0;
    if (!anyChange) return stats;

    await replaceSlideFromZip(context, zip, slideId);
  } catch {
    // Same best-effort/degraded-feature rationale as runOoxmlSlidePass above.
    logEvent("connector.action.invoke", "warn", {
      connectorSystemName: APPLY_BRAND_CONNECTOR,
      reason: "brand-apply-ooxml",
    });
  }

  return stats;
}

function transformSlideXml(orig: string, brand: BrandSpec, cfg: OoxmlPassConfig, stats: OoxmlStats): string {
  // Preserve the slide background exactly as-is: mask <p:bg> before any
  // transformation and restore it right before writing back, so no colour,
  // shadow, or gradient pass can repaint the backdrop.
  const bgBlocks: string[] = [];
  let xml = orig.replace(/<p:bg\b[^>]*\/>|<p:bg\b[\s\S]*?<\/p:bg>/g, (m) => {
    bgBlocks.push(m);
    return `<!--AIVA_BG_${bgBlocks.length - 1}-->`;
  });

  xml = applyTableAndColourRewrites(xml, brand, cfg, stats);
  xml = applyFontAndBulletRewrites(xml, brand, cfg);
  xml = applyStyleRemovals(xml, brand, cfg, stats);

  // Restore the untouched background before comparing/writing.
  if (bgBlocks.length > 0) {
    xml = xml.replace(/<!--AIVA_BG_(\d+)-->/g, (_m, i: string) => bgBlocks[Number(i)] ?? "");
  }
  return xml;
}

function applyTableAndColourRewrites(xml: string, brand: BrandSpec, cfg: OoxmlPassConfig, stats: OoxmlStats): string {
  let out = xml;

  // Apply table header style
  if (cfg.hasTableFormat && out.includes("<a:tbl")) {
    const result = applyTableStyleOoxml(out, brand);
    if (result.count > 0) { out = result.xml; stats.tableHeaders += result.count; stats.tables += result.count; }
  }

  // Snap all srgbClr values in the slide to nearest brand colour
  // (catches shapes/connectors that Office.js fill API missed, and also
  //  snaps table cell fills that aren't covered by applyTableStyleOoxml)
  if (cfg.hasColors) {
    const { xml: snapped, count } = snapColours(out, brand);
    if (count > 0) { out = snapped; }
    // Convert theme colour references to explicit brand colours — the deck's
    // theme itself stays unbranded, so schemeClr-based fills (table styles,
    // style refs) would otherwise keep their old colours.
    const { xml: mapped } = mapSchemeColours(out, brand);
    out = mapped;
  }
  return out;
}

function applyFontAndBulletRewrites(xml: string, brand: BrandSpec, cfg: OoxmlPassConfig): string {
  let out = xml;

  // Rewrite fonts across the whole slide (body + table cells)
  const bodyFont = cfg.bodyFont;
  if (bodyFont) out = rewriteFonts(out, bodyFont, cfg.preserveFonts);

  // Rewrite bullet chars (pass 1) and bullet font face (pass 2)
  const buChar = brand.bulletLevels?.[0]?.bulletChar;
  if (buChar) {
    out = out.replace(/<a:buChar\s+char="([^"]*)"/g, (_m, ch: string) => {
      if (!ch || ch.trim() === "" || (ch.codePointAt(0) ?? 0) < 32) return _m;
      return `<a:buChar char="${buChar}"`;
    });
  }
  if (bodyFont) {
    out = out.replace(/(<a:pPr\b[^>]*>[\s\S]*?<\/a:pPr>)/g, (pPrBlock: string) =>
      pPrBlock.replace(/<a:buFont\b([^>]*)\btypeface="[^"]*"/g, `<a:buFont$1 typeface="${bodyFont}"`),
    );
  }
  return out;
}

function applyStyleRemovals(xml: string, brand: BrandSpec, cfg: OoxmlPassConfig, stats: OoxmlStats): string {
  let out = xml;
  if (cfg.removeShadows) {
    const r = removeShadowsXml(out);
    out = r.xml;
    stats.shadows = r.count;
  }
  if (cfg.removeGradients) {
    const r = flattenGradientsXml(out, brand);
    out = r.xml;
    stats.gradients = r.count;
  }
  if (cfg.removeCorners) {
    const r = sharpenCornersXml(out);
    out = r.xml;
    stats.corners = r.count;
  }
  if (cfg.removeAnimations) {
    const r = removeAnimationsXml(out);
    out = r.xml;
    stats.animations = r.count;
  }
  return out;
}

function removeShadowsXml(xml: string): { xml: string; count: number } {
  let count = 0;
  let out = xml.replace(/<a:effectLst>[\s\S]*?<\/a:effectLst>/g, () => { count++; return ""; });
  out = out.replace(/<a:effectLst\s*\/>/g, () => { count++; return ""; });
  return { xml: out, count };
}

// Flatten gradients → solid, keeping the first stop's colour (or its theme ref)
function flattenGradientsXml(xml: string, brand: BrandSpec): { xml: string; count: number } {
  let count = 0;
  const out = xml.replace(/<a:gradFill[^>]*>[\s\S]*?<\/a:gradFill>/g, (block) => {
    const gsMatch = /<a:gs\b[^>]*>[\s\S]*?<\/a:gs>/.exec(block);
    let color = brand.colors.accent1 ?? "4472C4";
    if (gsMatch) {
      const srgbM = /<a:srgbClr\s+val="([0-9A-Fa-f]{6})"/.exec(gsMatch[0]);
      const schemeM = /<a:schemeClr\s+val="([^"]*)"/.exec(gsMatch[0]);
      if (srgbM?.[1]) color = srgbM[1];
      else if (schemeM?.[1]) { count++; return `<a:solidFill><a:schemeClr val="${schemeM[1]}"/></a:solidFill>`; }
    }
    count++;
    return `<a:solidFill><a:srgbClr val="${color}"/></a:solidFill>`;
  });
  return { xml: out, count };
}

function sharpenCornersXml(xml: string): { xml: string; count: number } {
  let count = 0;
  let out = xml.replace(/<a:prstGeom\s+prst="roundRect"[^>]*>[\s\S]*?<\/a:prstGeom>/g, () => { count++; return `<a:prstGeom prst="rect"><a:avLst/></a:prstGeom>`; });
  out = out.replace(/<a:prstGeom\s+prst="roundRect"[^/]*\/>/g, () => { count++; return `<a:prstGeom prst="rect"/>`; });
  return { xml: out, count };
}

function removeAnimationsXml(xml: string): { xml: string; count: number } {
  let count = 0;
  const out = xml.replace(/<p:timing>[\s\S]*?<\/p:timing>/g, () => { count++; return ""; });
  return { xml: out, count };
}

/**
 * Snap colours / convert theme refs / rewrite fonts inside every zip part
 * matching `pattern` (embedded charts, SmartArt diagram parts). Returns how
 * many parts changed.
 */
async function rebrandZipParts(zip: PptxZip, pattern: RegExp, brand: BrandSpec, cfg: OoxmlPassConfig): Promise<number> {
  const paths = Object.keys(zip).filter((p) => pattern.test(p));
  let changed = 0;
  for (const path of paths) {
    const bytes = zip[path];
    if (!bytes) continue;
    const orig = strFromU8(bytes);
    let xml = orig;
    if (cfg.hasColors) {
      const { xml: snapped } = snapColours(xml, brand);
      const { xml: mapped } = mapSchemeColours(snapped, brand);
      xml = mapped;
    }
    if (cfg.bodyFont) xml = rewriteFonts(xml, cfg.bodyFont, cfg.preserveFonts);
    if (xml !== orig) { zip[path] = strToU8(xml); changed++; }
  }
  return changed;
}

// insertSlidesFromBase64 inserts the modified copy AFTER targetSlideId,
// so the ORIGINAL keeps its index and the copy lands right behind it.
// Delete the original by ID so the copy takes its place — deleting by
// index+1 here would delete the freshly inserted copy instead.
async function replaceSlideFromZip(context: PowerPoint.RequestContext, zip: PptxZip, slideId: string): Promise<void> {
  const newBase64 = await zipPptxToBase64(zip);

  context.presentation.insertSlidesFromBase64(newBase64, {
    targetSlideId: slideId,
    formatting: "UseDestinationTheme" as PowerPoint.InsertSlideFormatting,
  });
  await context.sync();

  const original = context.presentation.slides.getItemOrNullObject(slideId);
  original.load("id");
  await context.sync();
  if (!original.isNullObject) {
    original.delete();
    await context.sync();
  }
}

// ─── OOXML colour snap ───────────────────────────────────────────────────────

export function snapColours(xml: string, brand: BrandSpec): { xml: string; count: number } {
  const candidates = [
    brand.colors.accent1, brand.colors.accent2, brand.colors.accent3,
    brand.colors.accent4, brand.colors.accent5, brand.colors.accent6,
    brand.colors.dk1, brand.colors.dk2,
  ].map((c) => normalizeHex(c)).filter((c): c is string => c !== null);

  if (candidates.length === 0) return { xml, count: 0 };

  // Skip bullet colour blocks (intentional bullet colours) and gradient fills
  // (decorative backdrops) so neither gets remapped.
  const segments = xml.split(/(<a:buClr\b[\s\S]*?<\/a:buClr>|<a:gradFill\b[^>]*>[\s\S]*?<\/a:gradFill>)/g);
  let count = 0;
  for (let i = 0; i < segments.length; i++) {
    if (i % 2 === 1) continue; // buClr / gradFill block — skip
    segments[i] = segments[i]!.replace(
      /<a:srgbClr\s+val="([0-9A-Fa-f]{6})"(\s*\/?>)/g,
      (_match, hex: string, tail: string) => {
        if (isNearWhite(hex, APPLY_BRAND_NEAR_WHITE_THRESHOLD) || isNearBlack(hex)) return _match;
        const nearest = findNearestBrandColor(hex, candidates);
        if (nearest) {
          const nearestHex = normalizeHex(nearest.replace("#", ""));
          if (nearestHex && nearestHex.toUpperCase() !== hex.toUpperCase()) {
            count++;
            return `<a:srgbClr val="${nearestHex}"${tail}`;
          }
        }
        return _match;
      },
    );
  }
  return { xml: segments.join(""), count };
}

// ─── OOXML scheme-colour mapping ─────────────────────────────────────────────

// Theme slot → BrandColors key. Text/background aliases map onto dk/lt slots.
const SCHEME_SLOT_MAP: Record<string, keyof BrandSpec["colors"]> = {
  accent1: "accent1", accent2: "accent2", accent3: "accent3",
  accent4: "accent4", accent5: "accent5", accent6: "accent6",
  dk1: "dk1", dk2: "dk2", lt1: "lt1", lt2: "lt2",
  tx1: "dk1", tx2: "dk2", bg1: "lt1", bg2: "lt2",
};

/**
 * Replace theme colour references (`<a:schemeClr val="accentN"/>`) with the
 * explicit brand hex for that slot. The presentation's own theme is NOT
 * rebranded, so theme-linked fills (table styles, SmartArt, style refs) keep
 * their old colours unless converted to explicit srgbClr values here.
 * Child transforms (lumMod, shade, tint…) are preserved — they are equally
 * valid inside srgbClr.
 */
export function mapSchemeColours(xml: string, brand: BrandSpec): { xml: string; count: number } {
  let count = 0;
  // Gradient fills are decorative (background washes, orbs) — leave their
  // colour stops untouched so applying the brand doesn't repaint backdrops.
  const segments = xml.split(/(<a:gradFill\b[^>]*>[\s\S]*?<\/a:gradFill>)/g);
  for (let i = 0; i < segments.length; i++) {
    if (i % 2 === 1) continue; // gradFill block — skip
    segments[i] = segments[i]!.replace(
      /<a:schemeClr\s+val="(\w+)"\s*\/>|<a:schemeClr\s+val="(\w+)"\s*>([\s\S]*?)<\/a:schemeClr>/g,
      (match, selfSlot: string | undefined, openSlot: string | undefined, children: string | undefined) => {
        const slot = selfSlot ?? openSlot ?? "";
        const key = SCHEME_SLOT_MAP[slot];
        if (!key) return match;
        const hex = normalizeHex(brand.colors[key] ?? "");
        if (!hex) return match;
        count++;
        return selfSlot !== undefined
          ? `<a:srgbClr val="${hex}"/>`
          : `<a:srgbClr val="${hex}">${children ?? ""}</a:srgbClr>`;
      },
    );
  }
  return { xml: segments.join(""), count };
}

// ─── OOXML font rewrite ───────────────────────────────────────────────────────

/**
 * Rewrite run fonts to the brand body font — EXCEPT typefaces in
 * `preserveFonts` (the brand title font, applied moments earlier by the
 * Office.js pass; rewriting it here reverted every title to the body font).
 */
export function rewriteFonts(xml: string, bodyFont: string, preserveFonts: string[] = []): string {
  const preserve = new Set(preserveFonts.filter(Boolean).map((f) => f.toLowerCase()));
  const sub = (match: string, pre: string, face: string): string =>
    preserve.has(face.toLowerCase()) ? match : `${pre}typeface="${bodyFont}"`;
  let out = xml;
  out = out.replace(/(<a:latin\b[^>]*?)\btypeface="([^"]*)"/g, sub);
  out = out.replace(/(<a:ea\b[^>]*?)\btypeface="([^"]*)"/g, sub);
  out = out.replace(/(<a:cs\b[^>]*?)\btypeface="([^"]*)"/g, sub);
  return out;
}

// ─── Image compression ────────────────────────────────────────────────────────

async function compressMediaImages(zip: PptxZip): Promise<{ saved: number; count: number }> {
  let saved = 0;
  let count = 0;
  const SKIP_EXTS = new Set(["svg", "emf", "wmf", "wdp", "tiff", "tif", "bmp", "jxr"]);

  const mediaFiles = Object.keys(zip).filter((p) => p.startsWith("ppt/media/") && !p.endsWith("/"));

  for (const path of mediaFiles) {
    const ext = (path.split(".").pop() ?? "").toLowerCase();
    if (SKIP_EXTS.has(ext)) continue;
    const data = zip[path];
    if (!data) continue;
    if (data.byteLength < 50_000) continue;

    try {
      const mimeType = ext === "png" ? "image/png" : "image/jpeg";
      const blob = new Blob([data.slice()], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("decode"));
        img.src = url;
      });
      URL.revokeObjectURL(url);

      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext("2d")?.drawImage(img, 0, 0);
      const outBlob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", 0.8));
      if (!outBlob) continue;
      const outBuf = new Uint8Array(await outBlob.arrayBuffer());
      if (outBuf.byteLength < data.byteLength * 0.9) {
        saved += data.byteLength - outBuf.byteLength;
        zip[path] = outBuf;
        count++;
      }
    } catch { /* keep original */ }
  }

  return { saved, count };
}

// ─── Table style OOXML helper ────────────────────────────────────────────────

function applyTableStyleOoxml(
  xml: string,
  brand: BrandSpec,
): { xml: string; count: number } {
  const hdr = brand.tableFormat?.headerRow;
  const bdr = brand.tableFormat?.bodyRow;
  const borderColor = normalizeHex(brand.tableFormat?.borderColor ?? "") ?? "D2D0CE";
  const borderWidthPt = brand.tableFormat?.borderWidth ?? 1;
  if (!hdr) return { xml, count: 0 };

  let count = 0;
  const modified = xml.replace(/<a:tbl\b[\s\S]*?<\/a:tbl>/g, (tblBlock) => {
    let rowIdx = 0;
    const result = tblBlock.replace(/<a:tr\b[\s\S]*?<\/a:tr>/g, (rowBlock) => {
      const fmt = rowIdx === 0 ? hdr : bdr;
      rowIdx++;
      if (!fmt) return rowBlock;
      return rowBlock.replace(/<a:tc\b[\s\S]*?<\/a:tc>/g, (cellBlock) =>
        styleTableCell(cellBlock, fmt, borderColor, borderWidthPt),
      );
    });
    count++;
    return result;
  });

  return { xml: modified, count };
}

/**
 * Restyle a single `<a:tc>` cell: run properties (font colour / bold / face)
 * inside the txBody, and borders + fill inside the tcPr.
 *
 * Styled tables usually carry NO explicit cell fill (they inherit from the
 * table style part), so the fill must be inserted into tcPr, not just
 * substituted. tcPr schema order is: borders first, fill last. Existing
 * borders are REPLACED (not kept) so old colours/dashes/widths can't survive
 * the rebrand; the `ln[LRTB]\b` boundary leaves lnTlToBr/lnBlToTr diagonals
 * untouched.
 */
const CELL_BORDER_RE = /<a:ln[LRTB]\b[^>]*\/>|<a:ln[LRTB]\b[^>]*>[\s\S]*?<\/a:ln[LRTB]>/g;

function styleTableCell(cellBlock: string, fmt: TableRowFormat, borderColor: string, borderWidthPt: number): string {
  let cell = cellBlock;
  const fillHex = normalizeHex(fmt.fillColor);
  const fontHex = normalizeHex(fmt.fontColor);
  const fillXml = fillHex ? `<a:solidFill><a:srgbClr val="${fillHex}"/></a:solidFill>` : "";
  const borderW = Math.max(6350, Math.round(borderWidthPt * 12700)); // pt → EMU, min ½pt
  const mkBorder = (tag: string) =>
    `<a:${tag} w="${borderW}" cap="flat" cmpd="sng" algn="ctr"><a:solidFill><a:srgbClr val="${borderColor}"/></a:solidFill><a:prstDash val="solid"/></a:${tag}>`;
  const borders = ["lnL", "lnR", "lnT", "lnB"].map(mkBorder).join("");

  // 1. Font colour / bold / face on every run — restricted to the txBody so
  //    tcPr content is never touched. Handles self-closing <a:rPr .../> too.
  const runFill = fontHex ? `<a:solidFill><a:srgbClr val="${fontHex}"/></a:solidFill>` : "";
  const latin = fmt.fontName?.trim() && !fmt.fontName.startsWith("+")
    ? `<a:latin typeface="${fmt.fontName}"/>` : "";
  cell = cell.replace(/<a:txBody>[\s\S]*?<\/a:txBody>/g, (txBody) => {
    // Normalise self-closing <a:rPr .../> to paired form so one rewrite pass
    // below handles both shapes without a backtracking-prone alternation.
    const expanded = txBody.replace(/<a:rPr\b[^>]*>/g, (tag) =>
      tag.endsWith("/>") ? `${tag.slice(0, -2)}></a:rPr>` : tag,
    );
    return expanded.replace(
      /<a:rPr\b([^>]*)>([\s\S]*?)<\/a:rPr>/g,
      (_m, rawAttrs: string, children: string) => {
        let attrs = rawAttrs.replace(/\sb="[01]"/g, "");
        attrs += ` b="${fmt.bold ? "1" : "0"}"`;
        let inner = children;
        if (runFill) inner = inner.replace(/<a:solidFill>[\s\S]*?<\/a:solidFill>/g, "");
        if (latin) inner = inner.replace(/<a:latin\b[^>]*\/>/g, "");
        return `<a:rPr${attrs}>${runFill}${latin}${inner}</a:rPr>`;
      },
    );
  });

  // 2. Borders + fill inside tcPr (created when missing). Existing L/R/T/B
  //    borders are stripped first so the full ordered brand set replaces them.
  if (/<a:tcPr\b[^>]*\/>/.test(cell)) {
    cell = cell.replace(/<a:tcPr\b([^>]*)\/>/, `<a:tcPr$1>${borders}${fillXml}</a:tcPr>`);
  } else if (/<a:tcPr\b/.test(cell)) {
    cell = cell.replace(/(<a:tcPr\b[^>]*>)([\s\S]*?)(<\/a:tcPr>)/, (_m, open: string, inner: string, close: string) => {
      let body = inner.replace(CELL_BORDER_RE, "");
      if (fillXml) {
        body = body
          .replace(/<a:noFill\s*\/>/g, "")
          .replace(/<a:solidFill>[\s\S]*?<\/a:solidFill>/g, "")
          .replace(/<a:gradFill\b[^>]*>[\s\S]*?<\/a:gradFill>/g, "")
          .replace(/<a:pattFill\b[^>]*>[\s\S]*?<\/a:pattFill>/g, "")
          .replace(/<a:grpFill\s*\/>/g, "");
      }
      return `${open}${borders}${body}${fillXml}${close}`;
    });
  } else {
    cell = cell.replace(/<\/a:tc>\s*$/, `<a:tcPr>${borders}${fillXml}</a:tcPr></a:tc>`);
  }
  return cell;
}
